import { useCallback, useEffect, useState } from 'react';
import { SolutionOutlined, TeamOutlined } from '@ant-design/icons';
import { MAvatar } from '@Components/Base';
import { Col, Divider, Flex, Row, Typography } from 'antd';

import { parseDateTime } from '@Utils/transformDateTime';
import { ReactComponent as UserIcon } from '@Icons/additional/user.svg';
import { ReactComponent as HomeIcon } from '@Icons/additional/buildings.svg';
import { ReactComponent as CalendarIcon } from '@Icons/additional/date-picker.svg';
import { ReactComponent as LocationIcon } from '@Icons/additional/location.svg';
import { User } from 'types';
import { Balance } from '@Types/api/form/leaveRequest/Balance';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { getDefaultUserState } from '@Store/utils/getDefaultUserState';

import styles from './basic-info.module.css';
import SelectEmployeeComponent from './components/SelectEmployeeComponent';
import SelectModalEmployee from './components/SelectModalEmployee';

const AVATAR_SIZE = 64;

interface BasicInformationSectionProps {
  userInfo: User;
  selectedUser?: User | undefined;
  showSelectEmployee?: boolean;
  showLeaveBalance?: boolean;
  handleSearchEmpByNumber?: (empNumber?: string) => void;
  handleApplySelectedUser?: () => void;
  showExtraFields?: boolean;
  balance?: Balance;
}

const BasicInformationSection: React.FC<BasicInformationSectionProps> = ({
  userInfo,
  selectedUser,
  showSelectEmployee,
  showLeaveBalance,
  handleSearchEmpByNumber,
  handleApplySelectedUser,
  showExtraFields = false,
  balance,
}) => {
  const [openSelectEmployeeModal, setOpenSelectEmployeeModal] = useState(false);
  const [employeeUser, setEmployeeUser] = useState<User>();
  const [isMyselfUser, setIsMySelfUser] = useState(false);
  const { data: optionUsers } = useGetUsersListByNameQuery(null, {
    skip: !showSelectEmployee,
  });

  useEffect(() => {
    if (userInfo.empName !== selectedUser?.empName) {
      setEmployeeUser(selectedUser);
    }
  }, [userInfo, selectedUser]);

  const showModal = (): void => {
    setOpenSelectEmployeeModal(true);
  };

  const handleOk = (): void => {
    if (handleApplySelectedUser) {
      handleApplySelectedUser();
      setOpenSelectEmployeeModal(false);
    }
  };

  const handleCancel = (): void => {
    setEmployeeUser(getDefaultUserState());
    setOpenSelectEmployeeModal(false);
  };

  const handleEmployeeTabClick = useCallback((): void => {
    setEmployeeUser(isMyselfUser ? getDefaultUserState() : selectedUser);
    setIsMySelfUser(false);
  }, [selectedUser]);

  const handleMySelfTabClick = useCallback((): void => {
    if (handleSearchEmpByNumber) {
      handleSearchEmpByNumber();
    }
    setIsMySelfUser(true);
    setEmployeeUser(userInfo);
  }, [userInfo, handleSearchEmpByNumber]);

  return (
    <Row gutter={[20, 20]} className={styles.basicInformationContainer}>
      <Col span={24}>
        <Flex className={styles.userInfo}>
          <div>
            <MAvatar size={AVATAR_SIZE} />
          </div>
          <Flex
            vertical
            className={styles.userDesc}
            style={{ maxWidth: `calc(100% - ${AVATAR_SIZE}px)` }}
            justify="center"
          >
            <Typography.Text className={styles.userName} strong ellipsis>
              {userInfo.empName}
            </Typography.Text>
            <Typography.Text>{userInfo.occupation}</Typography.Text>
          </Flex>
        </Flex>
        {handleSearchEmpByNumber && (
          <SelectEmployeeComponent showModal={showModal} />
        )}
        <Divider className={styles.userDivider} />
      </Col>
      <Col span={24}>
        <Flex vertical className={styles.userBasicInfo}>
          <Typography.Text className={styles.userBasicInfoCaption} strong>
            Basic Information
          </Typography.Text>
          <Flex gap={20} className={styles.userBasicInfoItem}>
            <div className={styles.iconWrapper}>
              <UserIcon />
            </div>
            <Flex vertical>
              <Typography.Text type="secondary">
                Employee Number
              </Typography.Text>
              <Typography.Text strong>
                {userInfo.empNumber || EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Flex>
          </Flex>

          <Flex gap={20} className={styles.userBasicInfoItem}>
            <div className={styles.iconWrapper}>
              <HomeIcon />
            </div>
            <Flex vertical>
              <Typography.Text type="secondary">Company</Typography.Text>
              <Typography.Text strong>
                {userInfo.company || EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Flex>
          </Flex>

          <Flex gap={20} className={styles.userBasicInfoItem}>
            <div className={styles.iconWrapper}>
              <CalendarIcon />
            </div>
            <Flex vertical>
              <Typography.Text type="secondary">
                Date of Joining
              </Typography.Text>
              <Typography.Text strong>
                {userInfo.startDate
                  ? parseDateTime(userInfo.startDate)
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Flex>
          </Flex>

          <Flex gap={20} className={styles.userBasicInfoItem}>
            <div className={styles.iconWrapper}>
              <LocationIcon />
            </div>
            <Flex vertical>
              <Typography.Text type="secondary">Location</Typography.Text>
              <Typography.Text strong>
                {userInfo.currentLocation || EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Flex>
          </Flex>

          {showExtraFields ? (
            <>
              <Flex gap={20} className={styles.userBasicInfoItem}>
                <TeamOutlined />
                <Flex vertical>
                  <Typography.Text type="secondary">
                    Nationality
                  </Typography.Text>
                  <Typography.Text strong>
                    {userInfo.countryOrg || EMPTY_FIELD_PLACEHOLDER}
                  </Typography.Text>
                </Flex>
              </Flex>

              <Flex gap={20} className={styles.userBasicInfoItem}>
                <SolutionOutlined />
                <Flex vertical>
                  <Typography.Text type="secondary">
                    Passport No.
                  </Typography.Text>
                  <Typography.Text strong>
                    {userInfo.passportNo || EMPTY_FIELD_PLACEHOLDER}
                  </Typography.Text>
                </Flex>
              </Flex>
            </>
          ) : null}
        </Flex>
        <Divider className={styles.userDivider} />
      </Col>
      {showLeaveBalance && (
        <Flex vertical className={styles.availableSection} gap={24}>
          <Row gutter={[0, 10]}>
            <Typography.Text strong>Available Balance </Typography.Text>
            <Col span={24}>
              As On Today{' '}
              <Typography.Text strong>
                {Number.isFinite(balance?.asOnToday)
                  ? balance?.asOnToday
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Col>
            <Col span={24}>
              Ticket Balance{' '}
              <Typography.Text strong>
                {Number.isFinite(balance?.ticketBalance)
                  ? balance?.ticketBalance
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Col>
            <Col span={24}>
              Annual Leave for Selected Date{' '}
              <Typography.Text strong>
                {Number.isFinite(balance?.asOnDate)
                  ? balance?.asOnDate
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={[0, 10]}>
            <Typography.Text strong>Taken</Typography.Text>
            <Col span={24}>
              Annual Leave this Year{' '}
              <Typography.Text strong>
                {Number.isFinite(balance?.leaveThisYear)
                  ? balance?.leaveThisYear
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Col>
            <Col span={24}>
              Ticket this Year{' '}
              <Typography.Text strong>
                {Number.isFinite(balance?.ticketThisYear)
                  ? balance?.ticketThisYear
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography.Text>
            </Col>
          </Row>
        </Flex>
      )}
      <SelectModalEmployee
        handleCancel={handleCancel}
        handleOk={handleOk}
        openSelectEmployeeModal={openSelectEmployeeModal}
        handleSearchEmpByNumber={handleSearchEmpByNumber!}
        optionUsers={optionUsers || []}
        handleEmployeeTabClick={handleEmployeeTabClick}
        handleMySelfTabClick={handleMySelfTabClick}
        isMyselfUser={isMyselfUser}
        currentUser={employeeUser}
      />
    </Row>
  );
};

export default BasicInformationSection;
